import { Component, EventEmitter, OnInit, Output, ViewChild, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms'; // <--- เพิ่ม FormsModule สำหรับ ngModel
import liff from '@line/liff';
import { ZXingScannerComponent, ZXingScannerModule } from '@zxing/ngx-scanner';
import { ToastNotifService } from '../../../../../../core/services/toastNotif.service';
import { ToastCustomService } from '../../../../../../tools/services/toast/toast-custom/toastCustom.service';
import { BarcodeFormat } from '@zxing/browser';
import { TokenService } from '../../../../../../core/services/token.service';
import { CookieService } from 'ngx-cookie-service';
import { LanguageService } from '../../../../../../core/services/language.service';
import { MyVehicleResponse } from '../../../../../../core/models/information.model';
import { ActionApiEvResponse, ScanQREntriesPayload } from '../../../../../../core/models/ev.model';
import { Router } from '@angular/router';
import { ApiEvChargerService } from '../../../../../../core/services/api/Ev-chager/api-Ev-Carger';
import { ApiVehicleService } from '../../../../../../core/services/api/Vehicle/api-vehicle';
import { LiffService } from '../../../../../../core/services/liff.service';

type CameraLensKind = 'wide' | 'main' | 'multi' | 'tele' | 'back' | 'front';

interface CameraLens {
  device: MediaDeviceInfo;
  kind: CameraLensKind;
  labelTh: string;
  labelEn: string;
}

const LENS_ORDER: CameraLensKind[] = ['wide', 'main', 'multi', 'tele', 'back', 'front'];

@Component({
  selector: 'app-modal-ev-charger-scan-qr',
  standalone: true,
  imports: [CommonModule, ZXingScannerModule, FormsModule], // <--- เพิ่ม FormsModule
  templateUrl: './modal-ev-charger-scan-qr.component.html',
  styleUrls: ['./modal-ev-charger-scan-qr.component.css']
})
export class ModalEvChargerScanQrComponent implements OnInit, OnDestroy {

  @Output() closeModal = new EventEmitter<void>();
  @Output() save = new EventEmitter<{ messageAPI: ActionApiEvResponse, evEntriesByQRCode: ScanQREntriesPayload }>();


  @ViewChild('scanner') scanner!: ZXingScannerComponent;
  allowedFormats = [BarcodeFormat.QR_CODE];

  isLiffInitialized = false;
  isInLineApp = false;
  isScanningWeb = false;
  hasPermission = false;
  isLoadingCamera = false;
  isOpeningCamera = false;
  private liffReady: Promise<boolean> = Promise.resolve(false);

  currentLanguage: 'TH' | 'EN' = 'TH';
  private langSub?: Subscription;
  // --- ส่วนที่เพิ่มใหม่สำหรับจัดการกล้อง ---
  lenses: CameraLens[] = []; // เลนส์ที่ให้ผู้ใช้เลือก (กรองกล้องหน้าออกเมื่อมีกล้องหลัง)
  currentDevice: MediaDeviceInfo | undefined; // กล้องที่ถูกเลือกปัจจุบัน

  // --- Logic ใหม่ ---
  step: 'scan' | 'selectBox' | 'select' | 'confirm' = 'scan';
  userVehicles: MyVehicleResponse[] = [];
  selectedVehicle: MyVehicleResponse | null = null;
  isLoadingValidation = false;

  // --- API ---
  location: string = '';
  station: string = '';
  qrCodeResult: string | null = null;
  evEntriesByQRCode: ScanQREntriesPayload = {
    uid: '',
    code: '',
    vehicleId: 0,
    license: ''
  }

  token: string = '';
  uid: string = '';

  constructor(
    private toast: ToastNotifService,
    private toastCustom: ToastCustomService,
    private apiEvChargerService: ApiEvChargerService,
    private apiVehicleService: ApiVehicleService,
    private tokenService: TokenService,
    private cookie: CookieService,
    private languageService: LanguageService,
    private router: Router,
    private liffService: LiffService
  ) { }

  ngOnInit(): void {
    this.uid = this.cookie.get('line_uid') || localStorage.getItem('line_uid') || '';
    // JWT is HttpOnly cookie — token_user no longer used
    this.token = '';
    this.initLiff();

    this.langSub = this.languageService.currentLang$.subscribe(lang => {
      this.currentLanguage = lang;
    });
  }

  ngOnDestroy(): void {
    if (this.langSub) {
      this.langSub.unsubscribe();
    }
  }

  async initLiff() {
    try {
      this.isInLineApp = liff.isInClient();
    } catch (error) {
      console.error('LIFF isInClient error', error);
      this.isInLineApp = false;
    }
    this.liffReady = this.liffService.ensureReady();
    this.isLiffInitialized = await this.liffReady;
  }

  onClose() {
    this.isScanningWeb = false;
    this.resetState();
    this.closeModal.emit();
  }

  onSave(messageAPI: ActionApiEvResponse, evEntriesByQRCode: ScanQREntriesPayload) {
    this.isLoadingValidation = false;
    this.save.emit({ messageAPI, evEntriesByQRCode });
    this.resetState();
    this.onClose();
  }

  resetState() {
    this.step = 'scan';
    this.qrCodeResult = null;
    this.selectedVehicle = null;
    this.userVehicles = [];
    this.isLoadingValidation = false;
  }

  get canUseLineScanner(): boolean {
    try {
      return this.isInLineApp && this.isLiffInitialized && liff.isApiAvailable('scanCodeV2');
    } catch {
      return false;
    }
  }

  async onScanQr() {
    if (this.isOpeningCamera) return;
    this.isOpeningCamera = true;
    try {
      // ขอสิทธิ์เองก่อน เพราะ zxing ส่ง error ผ่าน permissionResponse.error() ซึ่ง (permissionResponse) ใน template รับไม่ได้
      if (await this.requestCameraAccess()) {
        this.lenses = [];
        this.currentDevice = undefined;
        this.isScanningWeb = true;
        this.isLoadingCamera = true;
      }
    } finally {
      this.isOpeningCamera = false;
    }
  }

  async onScanWithLine() {
    if (this.isOpeningCamera) return;
    this.isOpeningCamera = true;
    try {
      await this.liffReady;
      await this.scanWithLiff();
    } finally {
      this.isOpeningCamera = false;
    }
  }

  private async requestCameraAccess(): Promise<boolean> {
    const th = this.currentLanguage === 'TH';
    if (!navigator.mediaDevices?.getUserMedia) {
      this.toastCustom.showWarning(
        th ? 'ไม่รองรับกล้อง' : 'Camera not supported',
        th ? 'เบราว์เซอร์นี้เปิดกล้องไม่ได้ กรุณาเปิดผ่านแอป LINE' : 'This browser cannot open the camera. Please open in LINE.'
      );
      return false;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      stream.getTracks().forEach(track => track.stop());
      return true;
    } catch (err: any) {
      console.warn('[ScanQR] getUserMedia failed', err);
      switch (err?.name) {
        case 'NotAllowedError':
        case 'SecurityError':
          this.toastCustom.showWarning(
            th ? 'ไม่ได้รับสิทธิ์ใช้กล้อง' : 'Permission Denied',
            th
              ? 'กรุณาอนุญาตการใช้กล้องให้ LINE / เบราว์เซอร์ ในการตั้งค่าเครื่อง แล้วลองใหม่'
              : 'Please allow camera access for LINE / your browser in device settings, then try again.'
          );
          break;
        case 'NotFoundError':
        case 'OverconstrainedError':
          this.toastCustom.showError('Error', th ? 'ไม่พบกล้องบนอุปกรณ์นี้' : 'No camera found on this device.');
          break;
        case 'NotReadableError':
        case 'AbortError':
          this.toastCustom.showError(
            'Error',
            th ? 'กล้องกำลังถูกใช้งานโดยแอปอื่น กรุณาปิดแอปนั้นแล้วลองใหม่' : 'Camera is in use by another app. Please close it and try again.'
          );
          break;
        default:
          this.toastCustom.showError('Error', th ? 'ไม่สามารถเปิดกล้องได้' : 'Unable to open the camera.');
      }
      return false;
    }
  }


  async scanWithLiff() {

    if (!liff.isInClient()) {
      this.toastCustom.showWarning(
        'Warning',
        'Please open this feature in LINE application.'
      );
      return;
    }

    if (!liff.isApiAvailable('scanCodeV2')) {
      this.toastCustom.showWarning(
        'Warning',
        'Your LINE version does not support QR scanning. Please update LINE.'
      );
      return;
    }

    try {
      const result = await liff.scanCodeV2();
      if (result?.value) {
        this.handleScanSuccess(result.value);
      }
    } catch (error: any) {
      if (error.code !== 'USER_CANCEL') {
        this.toastCustom.showError(
          'Error',
          'Failed to scan QR Code'
        );
      }
    }
  }


  // --- Web Browser Logic ---

  onCamerasFound(devices: MediaDeviceInfo[]): void {
    if (devices.length === 0) {
      this.isLoadingCamera = false;
      this.toastCustom.showError('Error', 'No camera found on this device.');
      this.isScanningWeb = false;
      return;
    }

    this.lenses = this.buildLenses(devices);
    this.currentDevice = this.pickDefaultLens(this.lenses)?.device;
  }

  /** zxing autostart ignores device changes while starting and picks the first "back" camera itself (may be telephoto / ultra wide) */
  onAutostarted(): void {
    this.isLoadingCamera = false;
    if (!this.scanner || !this.currentDevice) return;
    if (this.scanner.device?.deviceId !== this.currentDevice.deviceId) {
      this.scanner.device = this.currentDevice;
    }
    void this.resetZoom(this.currentDevice.deviceId);
  }

  selectLens(lens: CameraLens): void {
    if (!this.scanner || lens.device.deviceId === this.currentDevice?.deviceId) return;
    this.currentDevice = lens.device;
    this.scanner.device = lens.device;
    void this.resetZoom(lens.device.deviceId);
  }

  lensLabel(lens: CameraLens): string {
    return this.currentLanguage === 'TH' ? lens.labelTh : lens.labelEn;
  }

  private buildLenses(devices: MediaDeviceInfo[]): CameraLens[] {
    let backIndex = 0;
    let lenses = devices.map((device, i): CameraLens => {
      const label = device.label || '';
      if (/front|user|facing front|truedepth/i.test(label)) {
        return { device, kind: 'front', labelTh: 'กล้องหน้า', labelEn: 'Front' };
      }
      if (/ultra\s*wide/i.test(label)) {
        return { device, kind: 'wide', labelTh: 'มุมกว้าง 0.5x', labelEn: 'Wide 0.5x' };
      }
      if (/telephoto/i.test(label)) {
        return { device, kind: 'tele', labelTh: 'ซูม', labelEn: 'Zoom' };
      }
      if (/dual|triple/i.test(label)) {
        return { device, kind: 'multi', labelTh: 'อัตโนมัติ', labelEn: 'Auto' };
      }
      if (/^back camera$/i.test(label.trim())) {
        return { device, kind: 'main', labelTh: 'ปกติ 1x', labelEn: 'Normal 1x' };
      }
      backIndex++;
      return {
        device,
        kind: 'back',
        labelTh: label ? `กล้องหลัง ${backIndex}` : `กล้อง ${i + 1}`,
        labelEn: label ? `Back ${backIndex}` : `Camera ${i + 1}`,
      };
    });

    if (lenses.some(l => l.kind !== 'front')) {
      lenses = lenses.filter(l => l.kind !== 'front');
    }
    // iPhone Pro lists Dual / Dual Wide / Triple virtual cameras — keep one "Auto"
    const multi = lenses.filter(l => l.kind === 'multi');
    if (multi.length > 1) {
      const keep = multi.find(l => /triple/i.test(l.device.label))
        ?? multi.find(l => /dual wide/i.test(l.device.label))
        ?? multi[0];
      lenses = lenses.filter(l => l.kind !== 'multi' || l === keep);
    }
    return lenses.sort((a, b) => LENS_ORDER.indexOf(a.kind) - LENS_ORDER.indexOf(b.kind));
  }

  private pickDefaultLens(lenses: CameraLens[]): CameraLens | undefined {
    return lenses.find(l => l.kind === 'main')
      ?? lenses.find(l => l.kind === 'back' && /facing back|back|rear|environment/i.test(l.device.label))
      ?? lenses.find(l => l.kind === 'back')
      ?? lenses.find(l => l.kind === 'multi')
      ?? lenses[0];
  }

  /** Some devices remember a zoomed-in level — reset the live track to 1x when the browser supports the zoom constraint */
  private async resetZoom(deviceId: string): Promise<void> {
    for (let attempt = 0; attempt < 15; attempt++) {
      if (!this.isScanningWeb) return;
      const stream = this.scanner?.previewElemRef?.nativeElement?.srcObject;
      const track = stream instanceof MediaStream ? stream.getVideoTracks()[0] : undefined;
      const settingsDeviceId = track?.getSettings?.().deviceId;
      if (track && track.readyState === 'live' && (!settingsDeviceId || settingsDeviceId === deviceId)) {
        const zoom = (track.getCapabilities?.() as any)?.zoom as { min: number; max: number } | undefined;
        if (zoom) {
          const target = Math.min(Math.max(1, zoom.min), zoom.max);
          await track.applyConstraints({ advanced: [{ zoom: target } as any] }).catch(err =>
            console.warn('[ScanQR] reset zoom failed', err)
          );
        }
        return;
      }
      await new Promise(resolve => setTimeout(resolve, 200));
    }
  }

  onCamerasNotFound(): void {
    this.isLoadingCamera = false;
    this.isScanningWeb = false;
    this.toastCustom.showError(
      'Error',
      this.currentLanguage === 'TH' ? 'ไม่สามารถเชื่อมต่อกล้องได้ กรุณาลองใหม่' : 'Unable to connect to the camera. Please try again.'
    );
  }

  onPermissionResponse(permission: boolean) {
    this.hasPermission = permission;
    if (!permission) {
      this.isLoadingCamera = false;
      this.isScanningWeb = false;
      this.toastCustom.showWarning(
        'Permission Denied',
        'Please allow camera access in your browser settings.'
      );
    }
  }

  onCodeResult(resultString: string) {
    if (resultString) {
      this.handleScanSuccess(resultString);
    }
  }

  // --- Updated Logic: Parse -> Validate -> Fetch Vehicle ---

  handleScanSuccess(code: string) {
    // zxing ยิง scanSuccess ซ้ำได้ก่อนกล้องถูกปิด
    if (this.step !== 'scan' || this.isLoadingValidation) return;
    console.log('Raw QR Code:', code);
    this.isScanningWeb = false; // Stop camera

    const data = this.parseQrCode(code);
    if (!data) {
      this.toastCustom.showError('Error', 'Invalid QR Code format.');
      return;
    }

    this.qrCodeResult = data;
    this.checkStationChangerByQrCode();
  }

  parseQrCode(url: string): string | null {
    try {
      console.log("URL include : ", url.includes('data='));
      // Expecting format: https://.../...?data=1234
      if (url.includes('data=')) {
        const urlObj = new URL(url);
        this.qrCodeResult = urlObj.searchParams.get('data');
        return this.qrCodeResult;
      }
      this.qrCodeResult = url;
      return this.qrCodeResult; // Fallback: return as is (for dev/direct id)
    } catch (e) {
      console.warn('URL Parse error, using raw string', e);
      return url;
    }
  }
  selectVehicle(vehicle: MyVehicleResponse) {
    this.selectedVehicle = vehicle;
  }

  confirmSelection() {
    if (this.selectedVehicle && this.qrCodeResult) {
      this.evEntriesByQRCode.code = this.qrCodeResult || '';
      this.evEntriesByQRCode.vehicleId = this.selectedVehicle.id;
      this.evEntriesByQRCode.license = this.selectedVehicle.name;
      this.evEntriesByQRCode.uid = this.uid;

      this.sentEV_EntriesByQRCode();
    } else {
      this.toastCustom.showWarning('Warning', 'Please select a vehicle.');
    }
  }
  createVehicle() {
    this.router.navigate(['/vehicle-setting']);
  }

  /* --------------------------------------------- API --------------------------------------------- */
  // ส่ง QR Code
  checkStationChangerByQrCode() {
    this.isLoadingValidation = true;
    this.step = 'selectBox'; // Show loading UI

    // 1. Check License Charger
    this.apiEvChargerService.sentCheckStationChangerByQrCode(this.qrCodeResult || '', this.token).subscribe({
      next: (res) => {
        console.log("Response CheckStationChangerByQrCode :", res);
        if (res.isAvailable) { // Adjust based on actual API response
          this.location = res.siteName;
          this.station = res.stationName;

          // 2. Get My Vehicle
          this.getMyVehicle();

        } else {
          this.isLoadingValidation = false;
          this.step = 'scan';
          this.toastCustom.showError('Error', this.currentLanguage === 'TH' ? 'เลข QR Code ไม่ถูกต้อง' : 'QR Code is not valid.');
        }
      },
      error: (err) => {
        this.isLoadingValidation = false;
        this.step = 'scan';
        console.error(err);
        this.toastCustom.showError('Error', this.currentLanguage === 'TH' ? 'ไม่สามารถตรวจสอบชาร์จได้' : 'Failed to verify charger.');
      }
    });
  }
  // รับข้อมูลทะเบียนรถ
  getMyVehicle() {
    this.apiVehicleService.getMyVehicle(this.token, this.uid).subscribe({
      next: (vehicles) => {
        console.log("Response My Vehicles :", vehicles);
        this.isLoadingValidation = false;
        this.userVehicles = vehicles;
        this.selectedVehicle = this.userVehicles[0];
        this.step = 'select'; // Move to selection step
      },
      error: (err) => {
        this.isLoadingValidation = false;
        this.step = 'scan';
        console.error(err);
        this.toastCustom.showError('Error', 'Failed to fetch user vehicles.');
      }
    });
  }

  // ส่งทะเบียน
  sentEV_EntriesByQRCode() {
    this.isLoadingValidation = true;
    this.step = 'selectBox'; // Show loading UI

    // 1. Check License Charger
    this.apiEvChargerService.sentEV_EntriesByQRCode(this.evEntriesByQRCode, this.token).subscribe({
      next: (res) => {
        console.log("Response EV_EntriesByQRCode :", res);
        this.isLoadingValidation = false;
        if (res.status === 'ok') { // Adjust based on actual API response
          this.onSave(res, this.evEntriesByQRCode);
          this.onClose();

        } else {
          this.isLoadingValidation = false;
          this.step = 'select';
          this.toastCustom.showError('Error', res.message);
        }
      },
      error: (err) => {
        this.isLoadingValidation = false;
        this.step = 'scan';
        console.error(err);
        this.toastCustom.showError('Error', this.currentLanguage === 'TH' ? 'ไม่สามารถตรวจสอบชาร์จได้' : 'Failed to verify charger.');
      }
    });
  }

}
